/**
 * Quick Buy functionality for Symmetry theme
 * AIHIO Digital Oy
 */

(function() {
  'use strict';

  // Quick Buy Modal Class
  class QuickBuyModal {
    constructor() {
      this.modal = document.getElementById('quick-buy-modal');
      this.content = document.getElementById('quick-buy-content');
      this.overlay = document.getElementById('quick-buy-overlay');
      this.closeBtn = document.getElementById('quick-buy-close');
      this.isOpen = false;
      this.currentProductUrl = null;

      this.init();
    }

    init() {
      // Event delegation for quickbuy-toggle buttons
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('.quickbuy-toggle');
        if (toggle) {
          e.preventDefault();
          e.stopPropagation();
          const productUrl = toggle.getAttribute('href');
          if (productUrl) {
            this.open(productUrl);
          }
        }
      });

      // Close button
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      // Overlay click
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // ESC key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    }

    async open(productUrl) {
      if (!this.modal || !this.content) return;

      // Dispatch before-open event
      document.dispatchEvent(new CustomEvent('on:quickbuy:before-open', { 
        detail: { productUrl } 
      }));

      this.currentProductUrl = productUrl;
      this.content.innerHTML = '<div class="quick-buy__loading"><div class="spinner"></div></div>';
      this.modal.classList.add('is-open');
      document.body.classList.add('quick-buy-open');
      this.isOpen = true;

      try {
        // Fetch product page HTML
        const response = await fetch(productUrl);
        const html = await response.text();
        
        // Parse HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Products that need extra choices on the product page (e.g. aroma picker): no quick add
        if (doc.querySelector('[data-quickbuy-redirect]')) {
          const isEn = (document.documentElement.lang || '').toLowerCase().indexOf('en') === 0;
          this.content.innerHTML = `
            <div class="quick-buy__error">
              <p>${isEn ? 'Please choose your aromas on the product page.' : 'Valitse aromit tuotesivulla.'}</p>
              <a href="${productUrl}" class="btn btn--primary">${isEn ? 'Go to product page' : 'Siirry tuotesivulle'}</a>
            </div>
          `;
          document.dispatchEvent(new CustomEvent('on:quickbuy:after-open', { detail: { productUrl } }));
          return;
        }
        
        // Try to find product JSON in various formats used by Shopify themes
        let product = null;
        
        // Method 1: Look for data-product-json attribute
        const productJsonEl = doc.querySelector('[data-product-json]');
        if (productJsonEl) {
          product = JSON.parse(productJsonEl.textContent);
        }
        
        // Method 2: Look for script with type="application/json" containing product
        if (!product) {
          const scripts = doc.querySelectorAll('script[type="application/json"]');
          scripts.forEach(script => {
            try {
              const data = JSON.parse(script.textContent);
              if (data && data.id && data.variants) {
                product = data;
              }
            } catch (e) {}
          });
        }

        // Method 3: Look for product data in script tags
        if (!product) {
          const allScripts = doc.querySelectorAll('script:not([src])');
          allScripts.forEach(script => {
            const content = script.textContent;
            // Look for product object assignment
            const match = content.match(/var\s+product\s*=\s*(\{[\s\S]*?"variants"[\s\S]*?\});/);
            if (match) {
              try {
                product = JSON.parse(match[1]);
              } catch (e) {}
            }
          });
        }

        // Method 4: Fallback to .json endpoint and assume available
        if (!product) {
          const cleanUrl = productUrl.split('?')[0];
          const jsonUrl = cleanUrl + '.json';
          const jsonResponse = await fetch(jsonUrl);
          const jsonData = await jsonResponse.json();
          product = jsonData.product;
          
          // Mark all variants as available since .json doesn't include this
          if (product && product.variants) {
            product.variants = product.variants.map(v => ({
              ...v,
              available: true // Assume available, cart will show error if not
            }));
          }
        }

        if (product) {
          // Mark that prices are in euros (from .json endpoint)
          product._priceInCents = false;
          this.renderProduct(product, productUrl);
        } else {
          throw new Error('Product not found');
        }

      } catch (error) {
        console.error('Quick Buy error:', error);
        this.content.innerHTML = `
          <div class="quick-buy__error">
            <p>Tuotetta ei voitu ladata.</p>
            <a href="${productUrl}" class="btn btn--primary">Siirry tuotesivulle</a>
          </div>
        `;
      }

      // Dispatch after-open event
      document.dispatchEvent(new CustomEvent('on:quickbuy:after-open', { 
        detail: { productUrl } 
      }));
    }

    renderProduct(product, productUrl) {
      const hasVariants = product.variants.length > 1;
      const firstVariant = product.variants[0];
      const hasOnlyDefaultVariant = product.variants.length === 1 && 
        firstVariant.title === 'Default Title';

      // Get image - handle different JSON structures
      let featuredImage = '';
      
      // Structure 1: product.featured_image (string URL)
      if (product.featured_image && typeof product.featured_image === 'string') {
        featuredImage = product.featured_image;
      }
      // Structure 2: product.image.src (from .json endpoint)
      else if (product.image && product.image.src) {
        featuredImage = product.image.src;
      }
      // Structure 3: product.images[0].src (array of objects)
      else if (product.images && product.images.length > 0) {
        if (typeof product.images[0] === 'string') {
          featuredImage = product.images[0];
        } else if (product.images[0].src) {
          featuredImage = product.images[0].src;
        }
      }
      // Structure 4: product.media[0]
      else if (product.media && product.media.length > 0 && product.media[0].src) {
        featuredImage = product.media[0].src;
      }

      // Ensure HTTPS and proper size
      if (featuredImage) {
        featuredImage = featuredImage.replace(/^\/\//, 'https://');
        // Request a reasonable size image
        if (!featuredImage.includes('_')) {
          featuredImage = featuredImage.replace(/\.([^.]+)$/, '_600x.$1');
        }
      }

      // Format price - Shopify .json API returns price in store currency (euros)
      // e.g., "69.00" or "69" - NOT in cents
      const formatMoney = (price) => {
        if (price === null || price === undefined) return '0,00€';
        const numPrice = parseFloat(price);
        return numPrice.toFixed(2).replace('.', ',') + '€';
      };

      // Build variant options HTML
      let variantOptionsHtml = '';
      if (hasVariants && !hasOnlyDefaultVariant) {
        // Get option names - handle both array of strings and array of objects
        const optionNames = product.options.map(opt => {
          if (typeof opt === 'string') return opt;
          if (opt && opt.name) return opt.name;
          return 'Valinta';
        });

        optionNames.forEach((optionName, optionIndex) => {
          // Get unique values for this option
          const optionValues = [...new Set(product.variants.map(v => {
            if (v.options && v.options[optionIndex]) {
              return v.options[optionIndex];
            }
            // Fallback for different data structures
            const optKey = `option${optionIndex + 1}`;
            return v[optKey] || '';
          }).filter(Boolean))];

          if (optionValues.length > 0) {
            variantOptionsHtml += `
              <div class="quick-buy__option">
                <label class="quick-buy__option-label">${optionName}</label>
                <select class="quick-buy__option-select" data-option-index="${optionIndex}">
                  ${optionValues.map((value, i) => `<option value="${value}" ${i === 0 ? 'selected' : ''}>${value}</option>`).join('')}
                </select>
              </div>
            `;
          }
        });
      }

      // Check availability - default to true if not specified
      const isAvailable = firstVariant.available !== false;

      this.content.innerHTML = `
        <div class="quick-buy__product" data-product-id="${product.id}">
          <div class="quick-buy__image-col">
            ${featuredImage ? `
              <img src="${featuredImage}" alt="${product.title}" class="quick-buy__image" loading="lazy">
            ` : `
              <div class="quick-buy__image-placeholder"></div>
            `}
          </div>
          <div class="quick-buy__details-col">
            <h2 class="quick-buy__title">${product.title}</h2>
            
            ${firstVariant.sku ? `<div class="quick-buy__sku">SKU: ${firstVariant.sku}</div>` : '<div class="quick-buy__sku" style="display:none;"></div>'}
            
            <div class="quick-buy__price" data-price>
              ${firstVariant.compare_at_price && firstVariant.compare_at_price > firstVariant.price ? `
                <span class="quick-buy__price-compare">${formatMoney(firstVariant.compare_at_price)}</span>
              ` : ''}
              <span class="quick-buy__price-current ${firstVariant.compare_at_price && firstVariant.compare_at_price > firstVariant.price ? 'on-sale' : ''}">${formatMoney(firstVariant.price)}</span>
            </div>

            <form class="quick-buy__form" data-product-form>
              <input type="hidden" name="id" value="${firstVariant.id}" data-variant-id>
              
              ${variantOptionsHtml}
              
              <div class="quick-buy__quantity">
                <label class="quick-buy__quantity-label">Määrä</label>
                <div class="quick-buy__quantity-wrapper">
                  <button type="button" class="quick-buy__quantity-btn" data-action="decrease">−</button>
                  <input type="number" name="quantity" value="1" min="1" class="quick-buy__quantity-input" data-quantity>
                  <button type="button" class="quick-buy__quantity-btn" data-action="increase">+</button>
                </div>
              </div>

              <button type="submit" class="btn btn--primary quick-buy__submit" ${!isAvailable ? 'disabled' : ''} data-add-btn>
                ${isAvailable ? 'Lisää ostoskoriin' : 'Ei saatavilla'}
              </button>
            </form>

            <a href="${productUrl}" class="quick-buy__view-full">Näytä kaikki tiedot →</a>
          </div>
        </div>
      `;

      // Store variants data
      this.currentVariants = product.variants;
      this.currentOptions = product.options;
      this.currentImages = product.images || [];
      this.currentProduct = product;

      // Bind form events
      this.bindFormEvents();
    }

    bindFormEvents() {
      const form = this.content.querySelector('[data-product-form]');
      if (!form) return;

      // Variant selectors
      const selectors = form.querySelectorAll('.quick-buy__option-select');
      selectors.forEach(selector => {
        selector.addEventListener('change', () => this.updateVariant());
      });

      // Quantity buttons
      const decreaseBtn = form.querySelector('[data-action="decrease"]');
      const increaseBtn = form.querySelector('[data-action="increase"]');
      const quantityInput = form.querySelector('[data-quantity]');

      if (decreaseBtn && quantityInput) {
        decreaseBtn.addEventListener('click', () => {
          const current = parseInt(quantityInput.value) || 1;
          if (current > 1) quantityInput.value = current - 1;
        });
      }

      if (increaseBtn && quantityInput) {
        increaseBtn.addEventListener('click', () => {
          const current = parseInt(quantityInput.value) || 1;
          quantityInput.value = current + 1;
        });
      }

      // Form submit
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart(form);
      });
    }

    updateVariant() {
      const selectors = this.content.querySelectorAll('.quick-buy__option-select');
      const selectedOptions = Array.from(selectors).map(s => s.value);

      // Find matching variant - handle both 'options' array and 'option1', 'option2' format
      const matchingVariant = this.currentVariants.find(variant => {
        return selectedOptions.every((option, index) => {
          // Try options array first
          if (variant.options && variant.options[index]) {
            return variant.options[index] === option;
          }
          // Fallback to option1, option2, option3 format
          const optKey = `option${index + 1}`;
          return variant[optKey] === option;
        });
      });

      if (matchingVariant) {
        // Update variant ID
        const variantInput = this.content.querySelector('[data-variant-id]');
        if (variantInput) variantInput.value = matchingVariant.id;

        // Update price
        const priceContainer = this.content.querySelector('[data-price]');
        if (priceContainer) {
          const formatMoney = (price) => {
            if (price === null || price === undefined) return '0,00€';
            const numPrice = parseFloat(price);
            return numPrice.toFixed(2).replace('.', ',') + '€';
          };
          
          let priceHtml = '';
          if (matchingVariant.compare_at_price && parseFloat(matchingVariant.compare_at_price) > parseFloat(matchingVariant.price)) {
            priceHtml += `<span class="quick-buy__price-compare">${formatMoney(matchingVariant.compare_at_price)}</span>`;
          }
          priceHtml += `<span class="quick-buy__price-current ${matchingVariant.compare_at_price && parseFloat(matchingVariant.compare_at_price) > parseFloat(matchingVariant.price) ? 'on-sale' : ''}">${formatMoney(matchingVariant.price)}</span>`;
          
          priceContainer.innerHTML = priceHtml;
        }

        // Update image if variant has one
        const imageEl = this.content.querySelector('.quick-buy__image');
        if (imageEl && matchingVariant.featured_image) {
          let variantImage = matchingVariant.featured_image.src || matchingVariant.featured_image;
          if (typeof variantImage === 'string') {
            variantImage = variantImage.replace(/^\/\//, 'https://');
            if (!variantImage.includes('_')) {
              variantImage = variantImage.replace(/\.([^.]+)$/, '_600x.$1');
            }
            // Add loading state
            imageEl.classList.add('is-loading');
            imageEl.onload = () => imageEl.classList.remove('is-loading');
            imageEl.src = variantImage;
          }
        }

        // Update SKU if displayed
        const skuEl = this.content.querySelector('.quick-buy__sku');
        if (skuEl && matchingVariant.sku) {
          skuEl.textContent = `SKU: ${matchingVariant.sku}`;
          skuEl.style.display = 'block';
        } else if (skuEl) {
          skuEl.style.display = 'none';
        }

        // Update button - default to available if not specified
        const addBtn = this.content.querySelector('[data-add-btn]');
        if (addBtn) {
          const variantAvailable = matchingVariant.available !== false;
          addBtn.disabled = !variantAvailable;
          addBtn.textContent = variantAvailable ? 'Lisää ostoskoriin' : 'Ei saatavilla';
        }

        // Dispatch variant change event
        document.dispatchEvent(new CustomEvent('on:variant:change', {
          detail: { variant: matchingVariant }
        }));
      }
    }

    async addToCart(form) {
      const addBtn = form.querySelector('[data-add-btn]');
      const originalText = addBtn.textContent;
      
      addBtn.disabled = true;
      addBtn.textContent = 'Lisätään...';

      const formData = new FormData(form);
      const data = {
        id: formData.get('id'),
        quantity: formData.get('quantity') || 1
      };

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(data)
        });

        if (!response.ok) {
          throw new Error('Add to cart failed');
        }

        const result = await response.json();

        // Dispatch cart add event
        document.dispatchEvent(new CustomEvent('on:cart:add', { 
          detail: { item: result } 
        }));

        // Success feedback
        addBtn.textContent = 'Lisätty! ✓';

        // Close modal and open cart drawer after short delay
        setTimeout(() => {
          this.close();
          
          // Try to open cart drawer (Symmetry theme)
          if (typeof theme !== 'undefined' && theme.settings && theme.settings.cartType === 'drawer') {
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
          }
        }, 800);

      } catch (error) {
        console.error('Add to cart error:', error);
        addBtn.textContent = 'Virhe - yritä uudelleen';
        addBtn.disabled = false;
        
        // Dispatch error event
        document.dispatchEvent(new CustomEvent('on:cart:error', { 
          detail: { error: error.message } 
        }));

        setTimeout(() => {
          addBtn.textContent = originalText;
        }, 2000);
      }
    }

    close() {
      if (!this.modal) return;

      this.modal.classList.remove('is-open');
      document.body.classList.remove('quick-buy-open');
      this.isOpen = false;
      this.currentProductUrl = null;

      // Dispatch after-close event
      document.dispatchEvent(new CustomEvent('on:quickbuy:after-close'));
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new QuickBuyModal());
  } else {
    new QuickBuyModal();
  }

})();